import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";
import { SiteLogo } from "../site-logo";
import { useLocation } from "react-router";
import { PropsWithChildren, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import classNames from "classnames";
import { Pages } from "../../utils";
import { HashLink } from "react-router-hash-link";
import "./styles.scss";

const routesWithHiddenFooter = [
  Pages.DASHBOARD,
  Pages.ADMIN_DASHBOARD,
  Pages.DESIGNER_APPLICATION,
  Pages.SIGNUP,
  Pages.LOGIN,
  Pages.MY_DESIGN_FILES,
  Pages.ADD_DESIGNER_FILE,
  Pages.ADD_DESIGNER_FILE__BOARD,
  Pages.ADD_DESIGNER_FILE__BEFORE_AFTER,
  Pages.ADD_DESIGNER_FILE__PROJECT_PROFILE,
];

const SiteFooterLink = ({
  to,
  file,
  onClick,
  children,
  hashLink,
}: PropsWithChildren<{
  file?: string;
  to?: string;
  hashLink?: boolean;
  onClick?: () => void;
}>) => {
  if (to) {
    return (
      <Button
        component={HashLink}
        color={"black" as any}
        style={{ display: "block" }}
        variant="text"
        className="text-left footer-link"
        to={to}
      >
        <Typography variant="body1">{children}</Typography>
      </Button>
    );
  }
  if (onClick) {
    return (
      <Button
        color={"black" as any}
        style={{ display: "block" }}
        variant="text"
        className="text-left footer-link"
        onClick={onClick}
      >
        <Typography variant="body1">{children}</Typography>
      </Button>
    );
  }
  if (file) {
    return (
      <Button
        color={"black" as any}
        style={{ display: "block" }}
        variant="text"
        className="text-left footer-link"
        download={file}
        href={file}
      >
        <Typography variant="body1">{children}</Typography>
      </Button>
    );
  }
  return null;
};

const SiteFooterLinkColumn = ({
  links,
  title,
}: {
  links: {
    to?: string;
    text: string;
    file?: string;
    hashLink?: boolean;
    onClick?: () => void;
  }[];
  title: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Grid2
      size={{
        xs: 12,
        sm: 4,
        lg: 3,
      }}
      className="footer-column text-left"
    >
      <Stack spacing={2} className="footer-column text-left">
        <Typography
          variant="body2"
          component="h5"
          fontWeight={600}
          className="title font-body desktop-title"
        >
          {title}
        </Typography>
        <Stack
          className="mobile-links-header"
          direction="row"
          justifyContent="space-between"
        >
          <Typography
            variant="body2"
            component="h5"
            fontWeight={600}
            className="title font-body"
          >
            {title}
          </Typography>
          <Button
            variant="text"
            color={"black" as any}
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>
        </Stack>
        <div className={classNames("links-column-content", isOpen && "open")}>
          {links.map((link) => (
            <SiteFooterLink
              key={link.text}
              to={link.to}
              hashLink={!!link.hashLink}
              onClick={link.onClick}
            >
              {link.text}
            </SiteFooterLink>
          ))}
        </div>
      </Stack>
    </Grid2>
  );
};

export const SiteFooter = () => {
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);

  return (
    <Box
      component="section"
      className="site-footer"
      sx={{
        position: "relative",
        background: "#fff",
      }}
    >
      <div className="site-wrapper">
        <Grid2
          container
          columns={12}
          direction="row"
          spacing={4}
          justifyContent="space-between"
        >
          <Grid2
            size={{
              xs: 12,
              lg: 3,
            }}
            className="footer-column"
          >
            <SiteLogo />
          </Grid2>
          <SiteFooterLinkColumn
            links={[
              { to: Pages.ABOUT_US_PAGE, text: "About HDG" },
              {
                to: `${Pages.PRIVACY_POLICY}#informationWeCollect-5`,
                text: "Cookie Policy",
              },
              { to: Pages.COPYRIGHT_TRADEMARK, text: "Copyright & Trademark" },
              {
                text: "Accessibility",
                onClick: () => setIsAccessibilityOpen(true),
              },
            ]}
            title="Company"
          />
          <SiteFooterLinkColumn
            links={[
              { to: `${Pages.JOIN_AS_DESIGNER}#faq`, text: "Designer FAQ" },
              { to: Pages.LOGIN, text: "Sign in" },
            ]}
            title="Get Help"
          />
          <SiteFooterLinkColumn
            links={[
              {
                text: "Contact Us",
                to: Pages.CONTACT_US,
              },
              {
                to: "https://www.facebook.com/share/1DCEyW5aje/?mibextid=wwXIfr",
                text: "Facebook",
              },
              {
                to: "https://www.instagram.com/homedesignguild?igsh=MWNnazN3b3RuYTZ4aQ%3D%3D&utm_source=qr",
                text: "Instagram",
              },
              { to: "https://pin.it/4fil718nS", text: "Pinterest" },
              {
                to: `${Pages.HOME}#designer-confessions`,
                text: "Designer Confessions",
              },
            ]}
            title="Connect"
          />
        </Grid2>
      </div>
      <Stack
        direction={{
          xs: "column",
          md: "row",
        }}
        justifyContent="space-between"
        className="footer-bottom"
      >
        <nav>
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "flex-start",
              md: "center",
            }}
          >
            <SiteFooterLink to={Pages.TERMS_OF_SERVICE}>
              Terms of Service
            </SiteFooterLink>
            <SiteFooterLink to={Pages.PRIVACY_POLICY}>
              Privacy Policy
            </SiteFooterLink>
            {/* <SiteFooterLink file="/">Security</SiteFooterLink>
            <SiteFooterLink to="/">Site Map</SiteFooterLink> */}
          </Stack>
        </nav>
        <p>
          &copy;{new Date().getFullYear()} Home Design Guild. All rights
          reserved
        </p>
      </Stack>
      <Dialog
        open={isAccessibilityOpen}
        onClose={() => setIsAccessibilityOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Our Commitment to Accessibility</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2">
              Home Design Guild is committed to providing an accessible and
              user friendly experience to everyone. If you have difficulty
              viewing or navigating the content on this website, or notice any
              content, feature, or functionality that you believe is not fully
              accessible to people with disabilities, please contact us at
              info@homedesigngguild.com and provide a description of the
              specific feature or a suggestion for improvement.
            </Typography>
            <Typography variant="body2">
              We take your feedback seriously as we evaluate accommodations and
              our overall accessibility policies. Additionally, while our users
              upload much of the content found on Home Design Guild, our
              Acceptable Use Policy encourages users to provide content that is
              accessible and user friendly.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            color={"black" as any}
            variant="outlined"
            onClick={() => setIsAccessibilityOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
